import React, { useMemo } from "react"
import PropTypes from "prop-types"
import { Card, Col, Flex, Grid, Progress, Row, Tag, Typography } from "antd"
import { useTranslation } from "react-i18next"
import DataTable from "ui/DataTable"
import CustomPagination from "components/common/CustomPagination"
import { numberToCurrency } from "helpers/string"
import styles from "./TipsDetails.module.css"

const { Text } = Typography

function TipCard({ row, t }) {
  return (
    <div className={styles.card}>
      <Flex justify="space-between" align="flex-start" gap={8}>
        <Flex vertical gap={2} className={styles.cardHeader}>
          <Text strong className={styles.cardName}>
            {row.bookingRef}
          </Text>
          <Text type="secondary" className={styles.cardSub}>
            {row.paidDate}
          </Text>
        </Flex>
        <Text strong className={styles.cardAmount}>
          {numberToCurrency(row.tipAmount)}
        </Text>
      </Flex>
      <div className={styles.cardDivider} />
      <div className={styles.cardFields}>
        <Flex vertical gap={4} className={styles.cardField}>
          <Text type="secondary" className={styles.cardLabel}>
            {t("accounting.tip_report.card.experience")}
          </Text>
          <Flex vertical gap={0}>
            <Text className={styles.cardValue}>{row.experience}</Text>
            <Text type="secondary" className={styles.cardValueSub}>
              {row.experienceDate}
            </Text>
          </Flex>
        </Flex>
        <Flex vertical gap={4} className={styles.cardField}>
          <Text type="secondary" className={styles.cardLabel}>
            {t("accounting.tip_report.card.client")}
          </Text>
          <Text className={styles.cardValue}>{row.client}</Text>
        </Flex>
        {row.crewMembers.length > 0 && (
          <Flex vertical gap={4} className={`${styles.cardField} ${styles.cardFieldFull}`}>
            <Text type="secondary" className={styles.cardLabel}>
              {t("accounting.tip_report.card.crew_members")}
            </Text>
            <Flex gap={6} wrap>
              {row.crewMembers.map((m) => (
                <Tag key={`${row.key}-${m.profile_id}`} className={styles.crewTag}>
                  {m.name}
                </Tag>
              ))}
            </Flex>
          </Flex>
        )}
      </div>
    </div>
  )
}

const toTableRow = (record) => ({
  key: record.tip_transaction_id,
  tipTransactionId: record.tip_transaction_id,
  bookingRef: record.booking_reference || "-",
  experience: record.experience_name || "-",
  experienceDate: record.experience_date || "-",
  client: record.client_name || "-",
  crewMembers: Array.isArray(record.crew_members) ? record.crew_members : [],
  paidDate: record.paid_date || "-",
  tipAmount: Number.parseFloat(record.tip_amount) || 0
})

TipCard.propTypes = {
  row: PropTypes.shape({
    key: PropTypes.number,
    bookingRef: PropTypes.string,
    experience: PropTypes.string,
    experienceDate: PropTypes.string,
    client: PropTypes.string,
    crewMembers: PropTypes.array,
    paidDate: PropTypes.string,
    tipAmount: PropTypes.number
  }).isRequired,
  t: PropTypes.func.isRequired
}

TipsDetails.propTypes = {
  tipReport: PropTypes.arrayOf(
    PropTypes.shape({
      tip_transaction_id: PropTypes.number,
      booking_reference: PropTypes.string,
      experience_name: PropTypes.string,
      experience_date: PropTypes.string,
      client_name: PropTypes.string,
      crew_members: PropTypes.arrayOf(PropTypes.shape({ profile_id: PropTypes.number, name: PropTypes.string, amount: PropTypes.string })),
      paid_date: PropTypes.string,
      tip_amount: PropTypes.string
    })
  ),
  totalTips: PropTypes.string,
  totalCount: PropTypes.number,
  crewBreakdown: PropTypes.arrayOf(
    PropTypes.shape({
      profile_id: PropTypes.number,
      name: PropTypes.string,
      total: PropTypes.string,
      tip_count: PropTypes.number,
      percent: PropTypes.number
    })
  ),
  unassignedTips: PropTypes.shape({
    total_tip: PropTypes.string,
    tip_count: PropTypes.number
  }),
  metadata: PropTypes.shape({
    page: PropTypes.number,
    total_pages: PropTypes.number,
    next_page: PropTypes.number,
    prev_page: PropTypes.number
  }),
  tipPage: PropTypes.number,
  tipPageSize: PropTypes.number,
  onTipPageChange: PropTypes.func,
  onTipPageSizeChange: PropTypes.func
}

export default function TipsDetails({
  tipReport = [],
  totalTips = "0.00",
  totalCount = 0,
  crewBreakdown = [],
  unassignedTips = null,
  metadata = {},
  tipPage = 1,
  tipPageSize = 10,
  onTipPageChange,
  onTipPageSizeChange
}) {
  const { t } = useTranslation()
  const filteredRows = useMemo(() => tipReport.map(toTableRow), [tipReport])
  const screens = Grid.useBreakpoint()
  const isDesktop = screens.xl


  const tipCountLabel = (count) =>
    t("accounting.tip_report.summary.total_tips", {
      count,
      label: t("accounting.tip_report.side.tip_count", { count })
    })

  const columns = useMemo(
    () => [
      {
        title: t("accounting.tip_report.columns.booking_reference"),
        dataIndex: "bookingRef",
        key: "bookingRef",
        sorter: (a, b) => a.bookingRef.localeCompare(b.bookingRef),
        render: (value) => <Text strong>{value}</Text>
      },
      {
        title: t("accounting.tip_report.columns.experience"),
        key: "experience",
        sorter: (a, b) => a.experience.localeCompare(b.experience),
        render: (_, row) => (
          <Flex vertical className={styles.experienceCell}>
            <Text className={styles.truncateText}>{row.experience}</Text>
            <Text type="secondary" className={styles.cellSubtext}>
              {row.experienceDate}
            </Text>
          </Flex>
        )
      },
      {
        title: t("accounting.tip_report.columns.client"),
        dataIndex: "client",
        key: "client",
        sorter: (a, b) => a.client.localeCompare(b.client),
        render: (value) => <span className={styles.truncateText}>{value}</span>
      },
      {
        title: t("accounting.tip_report.columns.crew_members"),
        key: "crewMembers",
        render: (_, row) =>
          row.crewMembers.length ? (
            <Flex gap={6} wrap>
              {row.crewMembers.map((m) => (
                <Tag key={`${row.key}-${m.profile_id}`} className={styles.crewTag}>
                  {m.name}
                </Tag>
              ))}
            </Flex>
          ) : (
            <Text type="secondary">-</Text>
          )
      },
      {
        title: t("accounting.tip_report.columns.tip_paid_date"),
        dataIndex: "paidDate",
        key: "paidDate"
      },
      {
        title: t("accounting.tip_report.columns.tip_amount"),
        key: "tipAmount",
        align: "right",
        sorter: (a, b) => a.tipAmount - b.tipAmount,
        render: (_, row) => <Text strong>{numberToCurrency(row.tipAmount)}</Text>
      }
    ],
    [t]
  )

  const summaryRow = filteredRows.length ? (
    <tr className={styles.summaryRow}>
      <td colSpan={5} className="ant-table-cell">
        <Text strong>{tipCountLabel(totalCount)}</Text>
      </td>
      <td className={`ant-table-cell ${styles.summaryAmountCell}`}>
        <Text strong>{numberToCurrency(Number.parseFloat(totalTips))}</Text>
      </td>
    </tr>
  ) : null

  return (
    <Flex gap={16} align="start" className={`mt-m ${styles.layout}`}>
      <div className={styles.tableWrapper}>
        {isDesktop ? (
          <>
            <DataTable
              rowClickKey="key"
              dataSource={filteredRows}
              columns={columns}
              pagination={false}
              scroll={{ x: 980 }}
              className={styles.table}
              locale={{ emptyText: t("accounting.tip_report.empty") }}
              summary={() => summaryRow}
            />
            <CustomPagination
              metadata={metadata}
              pageSize={tipPageSize}
              total={totalCount}
              onPageChange={onTipPageChange}
              onPageSizeChange={onTipPageSizeChange}
            />
          </>
        ) : (
          <Flex vertical gap={12} className={styles.cardList}>
            {filteredRows.length ? (
              <>
                <Row gutter={[20, 20]}>
                  {filteredRows.map((row) => (
                    <Col key={row.key} xl={12} md={12} sm={24}>
                      <TipCard  row={row} t={t} />
                    </Col>
                  ))}
                </Row>
                <Flex justify="space-between" align="center" className={styles.cardSummary}>
                  <Text strong>{tipCountLabel(totalCount)}</Text>
                  <Text strong>{numberToCurrency(Number.parseFloat(totalTips))}</Text>
                </Flex>
                <CustomPagination
                  metadata={metadata}
                  pageSize={tipPageSize}
                  total={totalCount}
                  onPageChange={onTipPageChange}
                  onPageSizeChange={onTipPageSizeChange}
                />
              </>
            ) : (
              <Text type="secondary">{t("accounting.tip_report.empty")}</Text>
            )}
          </Flex>
        )}
      </div>

      <Card className={styles.sideCard}>
        <Flex vertical gap={12}>
          <div>
            <Text strong className={styles.sideTitle}>
              {t("accounting.tip_report.side.title")}
            </Text>
            <div>
              <Text type="secondary">{t("accounting.tip_report.side.subtitle")}</Text>
            </div>
          </div>
          <Row gutter={[20, 20]}>
            {crewBreakdown.length ? (
              crewBreakdown.map((entry) => {
                const entryTotal = Number.parseFloat(entry.total) || 0
                return (
                  <Col key={entry.profile_id} span={isDesktop ? 24 : 12} className={styles.crewRow}>
                    <Flex justify="space-between" align="center">
                      <Text className={styles.crewName}>{entry.name}</Text>
                      <Text strong>{numberToCurrency(entryTotal)}</Text>
                    </Flex>
                    <Progress
                      percent={entry.percent ?? 0}
                      showInfo={false}
                      size={{ height: 6 }}
                      strokeColor="var(--color-primary)"
                      trailColor="var(--color-lines)"
                    />
                    <Flex justify="flex-end">
                      <Text type="secondary" className={styles.tipCount}>
                        {t("accounting.tip_report.side.tip_count", { count: entry.tip_count })}
                      </Text>
                    </Flex>
                  </Col>
                )
              })
            ) : (
              <Col span={24}>
                <Flex justify="center" align="center" className={styles.noData}>
                  <Text type="secondary">{t("accounting.tip_report.side.no_data")}</Text>
                </Flex>
              </Col>
            )}
          </Row>

          {unassignedTips && Number.parseFloat(unassignedTips.total_tip) > 0 && (
            <div className={styles.crewRow}>
              <Flex justify="space-between" align="center">
                <Text type="secondary" className={styles.crewName}>
                  {t("accounting.tip_report.side.unassigned")}
                </Text>
                <Text>{numberToCurrency(Number.parseFloat(unassignedTips.total_tip))}</Text>
              </Flex>
              <Flex justify="flex-end">
                <Text type="secondary" className={styles.tipCount}>
                  {t("accounting.tip_report.side.tip_count", { count: unassignedTips.tip_count })}
                </Text>
              </Flex>
            </div>
          )}
        </Flex>
      </Card>
    </Flex>
  )
}
